import { ExpandMore, LocationOn } from '@mui/icons-material';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Chip,
    LinearProgress,
    Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAcceleratorsAccess } from '../../api/mobileapi';
import { AcceleratorAccessData, AccessPointAccelerators, AccessPointFacilityGroup } from '../../api/types';
import { GlobalError } from '../GlobalError/GlobalError';
import { useHomeData } from '../LoadHomeData/HomeDataProvider';
import { SituationIndicator } from '../SituationIndicator/SituationIndicator';
import { useTranslations } from '../Translations/TranslationsProvider';

interface StatusEntry {
    key: string;
    label: string;
    status?: boolean | string;
    text?: string;
}

const COLUMN_HEADERS = [
    { key: 'IMPACT' as const, label: 'IMPACT' },
    { key: 'situationBeforeIMPACT' as const, label: 'Situation Before IMPACT' },
    { key: 'Request' as const, label: 'Access Request' },
    { key: 'Training' as const, label: 'Training' },
    { key: 'Permits' as const, label: 'Permits' },
];

const getStatuses = (p: AccessPointAccelerators): StatusEntry[] =>
    COLUMN_HEADERS.map((h) => {
        const val = p[h.key as keyof AccessPointAccelerators];
        let text = undefined;
        if (typeof val === 'string') text = val;
        return { key: h.key, label: h.label, status: val, text };
    });

export const AccessToAcceleratorsPage = () => {
    const [selected, setSelected] = useState<string>();
    const [openLocations, setOpenLocations] = useState<Set<string>>(new Set());
    const { translations } = useTranslations();
    const { currentLanguage } = useHomeData();
    const navigate = useNavigate();

    const isPointValid = (p: AccessPointAccelerators): boolean =>
        p.accessPointSituation && p.IMPACT && p.situationBeforeIMPACT && p.Request && p.Training;

    if (!translations) {
        return null;
    }

    const {
        data: accessPointFacilities = [],
        error: acceleratorsAccessError,
        isLoading: isLoadingAcceleratorsAccess,
    } = useQuery<AcceleratorAccessData, Error, AccessPointFacilityGroup[]>({
        queryKey: ['accessPoints'],
        queryFn: () => fetchAcceleratorsAccess({ language: currentLanguage }),
        select: (data) => {
            return Object.values(
                data.accessPoints.reduce<Record<string, AccessPointFacilityGroup>>((acc, a) => {
                    if (!acc[a.accessPointComplex]) {
                        acc[a.accessPointComplex] = {
                            accessPointComplex: a.accessPointComplex,
                            sites: [],
                        };
                    }

                    const complex = acc[a.accessPointComplex];

                    let site = complex.sites.find((s) => s.accessPointSite === a.accessPointSite);

                    if (!site) {
                        site = {
                            accessPointSite: a.accessPointSite,
                            points: [],
                        };
                        complex.sites.push(site);
                    }

                    site.points.push(a);

                    return acc;
                }, {})
            ).sort((a, b) => a.accessPointComplex.localeCompare(b.accessPointComplex));
        },
    });

    useEffect(() => {
        setSelected(accessPointFacilities[0]?.accessPointComplex);
    }, [accessPointFacilities]);

    const toggle = (id: string) => {
        setOpenLocations((prev) => {
            const next = new Set(prev);
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const selectedFacility = accessPointFacilities.find((a) => a.accessPointComplex === selected) ?? null;

    if (isLoadingAcceleratorsAccess) {
        return <LinearProgress />;
    }

    if (acceleratorsAccessError) {
        return <GlobalError error={acceleratorsAccessError} translations={translations} />;
    }

    return (
        <Box className="min-h-screen" sx={{ backgroundColor: 'background.default' }}>
            <Box
                sx={{
                    paddingX: 2,
                    paddingY: 3,
                }}
            >
                <Typography variant="h5" color="text.primary" fontWeight="bold">
                    {translations.ATA_TITLE}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary" mt={0.5}>
                    {translations.ATA_SELECT_ACCELERATOR}
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, paddingX: 2, paddingY: 1, overflowX: 'auto' }}>
                    {accessPointFacilities.map((accessPointFacility, index) => (
                        <Button
                            key={index}
                            onClick={() => setSelected(accessPointFacility.accessPointComplex)}
                            sx={{
                                borderRadius: 2,
                                whiteSpace: 'nowrap',
                                backgroundColor:
                                    selected === accessPointFacility.accessPointComplex
                                        ? 'primary.main'
                                        : 'secondary.main',
                                color:
                                    selected === accessPointFacility.accessPointComplex
                                        ? 'primary.contrastText'
                                        : 'secondary.contrastText',
                                boxShadow: selected === accessPointFacility.accessPointComplex ? 3 : 0,
                                '&:hover': {
                                    backgroundColor:
                                        selected === accessPointFacility.accessPointComplex
                                            ? 'primary.dark'
                                            : 'secondary.dark',
                                },
                            }}
                        >
                            {accessPointFacility.accessPointComplex}
                        </Button>
                    ))}
                </Box>
            </Box>

            <Box>
                {selectedFacility?.sites.map((site) => {
                    const isOpen = openLocations.has(site.accessPointSite);
                    const invalidCount = site.points.filter((p) => !isPointValid(p)).length;
                    const validCount = site.points.filter((p) => isPointValid(p)).length;

                    return (
                        <Accordion
                            key={site.accessPointSite}
                            expanded={isOpen}
                            onChange={() => toggle(site.accessPointSite)}
                            disableGutters
                            sx={{
                                borderRadius: '20px !important',
                                '&:first-of-type': {
                                    borderRadius: '20px !important',
                                },
                                '&:last-of-type': {
                                    borderRadius: '20px !important',
                                },
                                '&:before': {
                                    display: 'none',
                                },
                                marginY: 1,
                            }}
                        >
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Box display="flex" alignItems="center" gap={2}>
                                    <LocationOn />
                                    <Box>
                                        <Typography variant="h6" color="text.primary" fontWeight="bold">
                                            {site.accessPointSite}
                                        </Typography>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            {site.points.length} {translations.ATA_ACCESS_POINTS}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box
                                    display="flex"
                                    gap={1}
                                    alignItems="center"
                                    ml="auto"
                                    mr={2}
                                    sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
                                >
                                    {invalidCount > 0 && (
                                        <Chip
                                            size="small"
                                            label={`${invalidCount} ${translations.ATA_INVALID}`}
                                            color="error"
                                            variant="filled"
                                            sx={{ fontWeight: 500 }}
                                        />
                                    )}

                                    {validCount > 0 && (
                                        <Chip
                                            size="small"
                                            label={`${validCount} ${translations.ATA_VALID}`}
                                            color="success"
                                            variant="filled"
                                            sx={{ fontWeight: 500 }}
                                        />
                                    )}
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ padding: 2 }}>
                                {site.points.map((point, index) => {
                                    const statuses = getStatuses(point);
                                    return (
                                        <Box
                                            key={index}
                                            sx={{
                                                borderTop: 1,
                                                borderColor: 'divider',
                                                paddingY: 2,
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => navigate(`/accesspoint/${point.accessPointCode}`)}
                                        >
                                            <Box display="flex" justifyContent="space-between" mt={1} mb={1}>
                                                <Box>
                                                    <Typography variant="h6">{point.accessPointCode}</Typography>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {point.permissionCode}
                                                    </Typography>
                                                </Box>
                                                <SituationIndicator
                                                    negativeStatusText={translations.ACCESS_POINT_STOP}
                                                    positiveStatusText={translations.ACCESS_POINT_GO}
                                                    situation={isPointValid(point)}
                                                    textOnly
                                                />
                                            </Box>
                                            <Box display="flex" gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
                                                {statuses
                                                    .filter((s) => !s.status)
                                                    .map((s) => (
                                                        <Box
                                                            key={s.key}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1.5,
                                                                paddingX: 1,
                                                                paddingY: 0.5,
                                                                backgroundColor: 'grey.100',
                                                                borderRadius: 1,
                                                            }}
                                                        >
                                                            {s.text ? (
                                                                <Typography variant="caption" color="primary">
                                                                    {s.text}
                                                                </Typography>
                                                            ) : (
                                                                <Chip
                                                                    color="error"
                                                                    size="small"
                                                                    sx={{
                                                                        width: 10,
                                                                        height: 10,
                                                                        borderRadius: '50%',
                                                                        minWidth: 0,
                                                                        padding: 0,
                                                                        '& .MuiChip-label': {
                                                                            display: 'none',
                                                                        },
                                                                    }}
                                                                />
                                                            )}
                                                            <Typography variant="caption" color="text.secondary">
                                                                {s.label}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </Box>
        </Box>
    );
};
